/**
 * Sélecteur de vitesse de jeu : ▶ (x1), ▶▶ (x2), ▶▶▶ (x3).
 *
 * Trois boutons à bascule (`aria-pressed`) dans un même groupe : le pictogramme dit la cadence,
 * le nom accessible dit la valeur (« Vitesse x2 »). Chaque bouton garde 44 × 44 px.
 */
import { useTranslation } from 'react-i18next';

import { VITESSES_JEU, type VitesseJeu } from '../jeu/vitesse';

/** Libellés accessibles, par cran (clés littérales : le contrôle i18n les retrouve). */
const LIBELLES = {
  1: 'jeu.vitesse.normale',
  2: 'jeu.vitesse.double',
  3: 'jeu.vitesse.triple',
} as const satisfies Record<VitesseJeu, string>;

/** Largeur d'une flèche et décalage entre deux flèches, en unités du viewBox (hauteur 24). */
const LARGEUR_FLECHE = 11;
const PAS_FLECHE = 8;

/** Une flèche « lecture » par cran : ▶, ▶▶, ▶▶▶. */
function IconeVitesse({ vitesse }: { readonly vitesse: VitesseJeu }) {
  const largeur = LARGEUR_FLECHE + (vitesse - 1) * PAS_FLECHE + 2;
  const fleches = Array.from({ length: vitesse }, (_, rang) => {
    const x = 1 + rang * PAS_FLECHE;
    return `M${String(x)} 5.5 ${String(x + LARGEUR_FLECHE)} 12 ${String(x)} 18.5Z`;
  });
  return (
    <svg
      className="vitesse__icone"
      viewBox={`0 0 ${String(largeur)} 24`}
      width={Math.round((largeur / 24) * 18)}
      height="18"
      aria-hidden="true"
      focusable="false"
    >
      <path
        d={fleches.join(' ')}
        fill="currentColor"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export interface ProprietesSelecteurVitesse {
  readonly vitesse: VitesseJeu;
  readonly onChange: (vitesse: VitesseJeu) => void;
}

export function SelecteurVitesse({ vitesse, onChange }: ProprietesSelecteurVitesse) {
  const { t } = useTranslation();

  return (
    <div
      className="vitesse"
      role="group"
      aria-label={t('jeu.vitesse.titre')}
      data-vitesse-jeu={vitesse}
    >
      {VITESSES_JEU.map((cran) => {
        const libelle = t(LIBELLES[cran]);
        return (
          <button
            key={cran}
            type="button"
            className="vitesse__bouton"
            aria-pressed={cran === vitesse}
            aria-label={libelle}
            title={libelle}
            data-vitesse={cran}
            onClick={() => {
              onChange(cran);
            }}
          >
            <IconeVitesse vitesse={cran} />
          </button>
        );
      })}
    </div>
  );
}
